import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A funnel chart is a data visualization tool used to represent stages in a process where
          the values progressively decrease. It is shaped like a funnel, with the widest part at the
          top representing the initial stage and the narrowest part at the bottom representing the
          final stage. This chart is ideal for visualizing processes like sales funnels, customer
          journeys, or any process with a series of steps, helping to highlight drop-offs or
          conversion rates between stages.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Funnel charts are perfect for illustrating:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Sequential Processes:</strong> Funnel charts effectively represent
            data moving through different stages, such as sales pipelines or recruitment funnels,
            making it easy to track progression or loss at each step.
          </li>
          <li>
            <strong>Identifying Drop-Offs:</strong> They are ideal for pinpointing areas where
            significant reductions occur, helping to detect inefficiencies or bottlenecks in
            processes like lead conversion or customer retention.
          </li>
          <li>
            <strong>Displaying Proportional Data:</strong> Funnel charts are useful for showing how
            data decreases or progresses proportionally across stages, such as tracking engagement
            rates or completion percentages in workflows.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Funnel charts are excellent for illustrating step-by-step processes, but they also have
          some significant limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Suitability for Complex Processes:</strong> Funnel charts are ideal for
            linear processes but may oversimplify more complex workflows with multiple branches or
            feedback loops.
          </li>
          <li>
            <strong>Potential Misrepresentation of Proportions:</strong> The visual tapering can
            exaggerate differences between stages, particularly when actual proportional changes are
            minimal, leading to misinterpretations.
          </li>
          <li>
            <strong>Lack of Detailed Insights:</strong> While funnel charts highlight drop-offs
            between stages, they do not inherently explain the causes, requiring supplemental
            analysis or visualizations for deeper understanding.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in funnel charts is essential for clearly distinguishing each stage of the
          process. Gradual color transitions from one stage to the next help represent the flow of
          data and the progression through different stages. Lighter colors can indicate earlier
          stages, while darker or more saturated colors can emphasize later stages, highlighting
          areas with the most significant data. Thoughtful color choices guide the viewer&apos;s
          attention to key points and improve the overall readability of the chart.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Funnel charts are excellent for illustrating sequential processes or conversions, but they
          also present distinct challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Detail:</strong> Funnel charts often oversimplify data, making it
            challenging to capture nuanced insights or variability within each stage.
          </li>
          <li>
            <strong>Proportional Misinterpretation:</strong> Differences in stage widths might be
            misinterpreted as absolute values rather than proportions, leading to potential
            misunderstanding.
          </li>
          <li>
            <strong>Ineffectiveness for Complex Processes:</strong> When processes have multiple
            branching paths or loops, funnel charts can become inadequate or overly simplistic.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
